import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";
import type { Tone } from "@/types/tone";

type TileTone =
  | "base"
  | "muted"
  | Extract<
      Tone,
      "neutral" | "positive" | "warning" | "negative" | "informative"
    >;

const tileVariants = cva("text-surface-on-surface", {
  variants: {
    tone: {
      base: ["bg-surface-container-lowest"],
      muted: ["bg-surface-container-low"],
      neutral: ["bg-surface-container"],
      positive: ["bg-positive-container", "text-positive-on-container"],
      warning: [
        "bg-accessibility-warning-container",
        "text-warning-on-container",
      ],
      negative: ["bg-negative-container", "text-negative-on-container"],
      informative: [
        "bg-informative-container",
        "text-informative-on-container",
      ],
    },
    padding: {
      none: "p-0",
      dense: "px-(--spacing-8) py-(--spacing-10)",
      compact: "p-(--spacing-12)",
      comfortable: "px-(--spacing-20) py-(--spacing-16)",
    },
    radius: {
      none: "rounded-(--radius-none)",
      sm: "rounded-(--radius-sm)",
      md: "rounded-(--radius-md)",
      lg: "rounded-(--radius-lg)",
      xl: "rounded-(--radius-xl)",
      full: "rounded-(--radius-full)",
    },
    aspect: {
      auto: null,
      square: "aspect-square",
    },
    centered: {
      true: "grid place-items-center",
      false: null,
    },
    width: {
      auto: null,
      content: "w-fit",
      full: "w-full",
    },
  },
  defaultVariants: {
    tone: "base",
    padding: "comfortable",
    radius: "sm",
    aspect: "auto",
    centered: false,
    width: "auto",
  },
});

type TileAspect = NonNullable<VariantProps<typeof tileVariants>["aspect"]>;
type TilePadding = NonNullable<VariantProps<typeof tileVariants>["padding"]>;
type TileRadius = NonNullable<VariantProps<typeof tileVariants>["radius"]>;
type TileWidth = NonNullable<VariantProps<typeof tileVariants>["width"]>;

export interface TileProps
  extends Omit<
      React.HTMLAttributes<HTMLDivElement>,
      "className" | "color" | "style"
    >,
    VariantProps<typeof tileVariants> {
  children?: React.ReactNode;
  hasBorder?: boolean;
}

/**
 * Lightweight token-backed content tile.
 *
 * Use when content needs a semantic background, padding, and radius without
 * alert semantics. Set `hasBorder` to add Card's border.
 *
 * @example
 * <Tile tone="base">Content</Tile>
 * <Tile tone="informative" padding="compact" radius="md">Content</Tile>
 * <Tile aspect="square" centered>Content</Tile>
 * <Tile width="content">Content-sized tile</Tile>
 */
function Tile({
  tone = "base",
  padding = "comfortable",
  radius = "sm",
  aspect = "auto",
  centered = false,
  width = "auto",
  hasBorder = false,
  children,
  ...props
}: TileProps) {
  return (
    <div
      data-slot="tile"
      data-tone={tone}
      data-padding={padding}
      data-radius={radius}
      data-aspect={aspect}
      data-centered={centered || undefined}
      data-width={width}
      className={cn(
        tileVariants({ tone, padding, radius, aspect, centered, width }),
        hasBorder && "border border-outline-subtle",
        "shadow-elevation-1",
      )}
      {...props}
    >
      {children}
    </div>
  );
}

export { Tile };
export type { TileAspect, TilePadding, TileRadius, TileTone, TileWidth };
